import { useEffect, useRef } from "react";

/**
 * Makes an open dialog/sheet/modal close on the phone's (or browser's) back
 * button instead of letting the back button navigate the underlying page
 * away — which previously could jump the app all the way back to another
 * screen, or even the login page, when a dialog was open on top.
 *
 * Usage:
 *   useBackClose(!!receiptRow, () => setReceiptRow(null));
 *
 * While `isOpen` is true, this pushes one extra history entry. Pressing
 * back pops that entry and calls `onClose` — the underlying page never
 * moves. If the dialog is closed some other way (Cancel/Save/tap outside),
 * the extra history entry is quietly removed so the history stack stays
 * clean and a later back press behaves normally.
 */
export function useBackClose(isOpen, onClose) {
  const closedByBackRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    window.history.pushState({ __dialog: true }, "");
    closedByBackRef.current = false;

    const handlePopState = () => {
      closedByBackRef.current = true;
      onClose();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (!closedByBackRef.current) {
        window.history.back();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
}
